import { useParams, Link, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { businessLineMap, businessLines } from "@/data/businessLines";

export default function BusinessLine() {
  const { slug } = useParams();
  const line = businessLineMap[slug];
  useSeo({
    title: line?.metaTitle,
    description: line?.metaDescription,
  });

  if (!line) return <Navigate to="/" replace />;
  const Icon = Icons[line.icon] || Icons.Building2;

  return (
    <div>
      {/* HEADER */}
      <section className="bg-[#0D1B2A] pt-16 pb-20 border-b border-white/5 relative noise-overlay">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft size={15} /> Torna alla home
          </Link>
          <Reveal>
            <Icon className="text-[#C5A059] mt-10" size={44} strokeWidth={1.5} />
            <h1 className="font-display font-light text-4xl lg:text-6xl text-white mt-6">
              {line.name}
            </h1>
            <p className="label-eyebrow mt-5">Pubblico target</p>
            <p className="text-gray-300 mt-2 text-lg max-w-2xl">{line.audience}</p>
          </Reveal>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="bg-[#1E2229] py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Il nostro approccio</span>
            <p className="text-gray-200 text-xl lg:text-2xl leading-relaxed mt-5 font-display font-light">
              {line.valueProp}
            </p>
            {line.complianceNote && (
              <p className="text-sm text-gray-400 leading-relaxed mt-8 border-l-2 border-[#C5A059]/50 pl-4">
                {line.complianceNote}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Come lavoriamo</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4">
              Il processo, in passi chiari
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px mt-14 bg-white/5">
            {line.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-[#0D1B2A] p-8 h-full">
                  <div className="font-serif-claim text-4xl text-[#C5A059]">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-lg text-white mt-4">{s.title}</h3>
                  <p className="text-sm text-gray-400 mt-3 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ETHICAL DIFFERENTIATOR */}
      <section className="bg-[#1E2229] py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="border border-[#C5A059]/30 bg-[#0D1B2A] p-10 lg:p-14 relative">
              <ShieldCheck className="text-[#C5A059]" size={36} strokeWidth={1.5} />
              <div className="label-eyebrow mt-6">Differenziatore etico / metodologico</div>
              <p className="font-serif-claim italic text-2xl lg:text-3xl text-white mt-4 leading-snug">
                {line.ethicalDiff}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Parliamone</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4">
              Richiedi informazioni su {line.name}
            </h2>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Compila il modulo: un socio ti risponderà con un'analisi concreta, senza formule
              generiche.
            </p>
            <div className="mt-10">
              <LeadForm
                businessLine={line.name}
                sourcePage={`Linea: ${line.name}`}
                showProfile
                showInvestorFields={line.slug === "advisory-club-deal"}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* OTHER LINES */}
      <section className="bg-[#1E2229] py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="label-eyebrow">Altre linee di business</span>
          <div className="flex flex-wrap gap-3 mt-6">
            {businessLines
              .filter((b) => b.slug !== slug)
              .map((b) => (
                <Link
                  key={b.slug}
                  to={`/linee/${b.slug}`}
                  data-testid={`other-line-${b.slug}`}
                  className="text-sm text-gray-300 border border-white/15 px-4 py-2 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                >
                  {b.name}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
